import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormControl, Validators } from '@angular/forms';
import { PersonEmailModel } from './person-email.model';
import {
  ModalConfirmationComponent,
  ModalConfirmationModel
} from 'src/shared-app-portal/components/modal-confirmation/modal-confirmation.component';

@Component({
  selector: 'app-email-editor',
  templateUrl: './email-editor.component.html',
  styleUrls: ['./email-editor.component.scss'],
  standalone: false
})
export class EmailEditorComponent implements OnChanges {
  @Input() manageOnlySecondaryEmails: boolean = false;
  @Input() ignoreVerification: boolean = false;
  @Input() ignoreAlertModals: boolean = false;
  @Input() readOnly: boolean = false;
  @Input() secondaryEmails: PersonEmailModel[] = [];
  @Input() primaryEmail: string = '';
  @Input() addFn: (email: string) => Promise<boolean>;
  @Input() deleteFn: (emailId: string) => Promise<boolean>;
  @Input() makePrimaryFn: (email: PersonEmailModel) => Promise<boolean>;
  @Output() verify = new EventEmitter<PersonEmailModel>();

  emailsItems: PersonEmailModel[] = [];

  isAddingEmail = false;
  isSaving = false;
  pendingDeleteId: string | null = null;
  pendingMakePrimaryId: string | null = null;
  addEmailControl = new FormControl('', [Validators.required, Validators.email]);

  private confirmationRef: MatDialogRef<ModalConfirmationComponent>;

  constructor(private dialog: MatDialog) {}
  ngOnChanges(changes: SimpleChanges) {
    if (changes['secondaryEmails'] || changes['primaryEmail']) {
      this.emailsItems = this.secondaryEmails?.map((e) => ({ ...e })) ?? [];
      if (this.ignoreVerification) {
        this.emailsItems.forEach((e) => (e.isVerified = true)); //if verification is not required, treat all emails as verified for display purposes
      }
      this.emailsItems.sort((a, b) => a.email.localeCompare(b.email));
    }
  }

  async confirmMakePrimary(email: PersonEmailModel) {
    if (this.ignoreAlertModals) {
      await this.makePrimary(email);
      return;
    }
    const data: ModalConfirmationModel = {
      simpleDialog: true,
      title: 'Make Primary Email',
      action: null,
      delete: false,
      okButtonText: 'Confirm',
      okButtonId: 'modal-make-primary',
      description: `Are you sure you want to make ${email.email} your primary email? All notifications will be sent to this address.`,
      textMatcherValue: ''
    };
    this.confirmationRef = this.dialog.open(ModalConfirmationComponent, { data });
    this.confirmationRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.makePrimary(email);
      }
      this.confirmationRef = null;
    });
  }

  showAddForm() {
    this.addEmailControl.reset();
    this.isAddingEmail = true;
  }

  cancelAdd() {
    this.isAddingEmail = false;
    this.addEmailControl.reset();
  }

  async submitAdd() {
    this.addEmailControl.markAsTouched();
    if (this.addEmailControl.invalid) return;

    const email = (this.addEmailControl.value as string).trim().toLowerCase();
    const allEmails = [
      this.primaryEmail?.toLowerCase(),
      ...(this.secondaryEmails?.map((e) => e.email?.toLowerCase()) ?? [])
    ];
    if (allEmails.includes(email)) {
      this.addEmailControl.setErrors({ duplicate: true });
      return;
    }

    this.isSaving = true;
    const success = await this.addFn(email);
    this.isSaving = false;
    if (success) {
      this.isAddingEmail = false;
      this.addEmailControl.reset();
    }
  }

  async confirmDelete(email: PersonEmailModel) {
    if (this.ignoreAlertModals) {
      await this.deleteEmail(email);
      return;
    }
    const data: ModalConfirmationModel = {
      simpleDialog: false,
      title: null,
      action: `delete ${email.email}`,
      delete: true,
      okButtonText: 'Delete',
      okButtonId: 'modal-email-editor',
      description: null,
      textMatcherValue: 'DELETE'
    };
    this.confirmationRef = this.dialog.open(ModalConfirmationComponent, { data });
    this.confirmationRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.deleteEmail(email);
      }
      this.confirmationRef = null;
    });
  }

  private async makePrimary(email: PersonEmailModel) {
    this.pendingMakePrimaryId = email.id;
    try {
      await this.makePrimaryFn(email);
    } finally {
      this.pendingMakePrimaryId = null;
    }
  }

  private async deleteEmail(email: PersonEmailModel) {
    this.pendingDeleteId = email.id;
    try {
      await this.deleteFn(email.id);
    } finally {
      this.pendingDeleteId = null;
    }
  }
}
